import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { getApiUrl } from '../config/api';
import '../App.css';

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await axios.post(getApiUrl('api/auth/login'), formData);

      if (response.data.success) {
        const { token, user } = response.data.data;
        
        // Save to context and localStorage
        login(token, user);
        
        setMessage({ 
          type: 'success', 
          text: 'Login successful! Redirecting...' 
        });
        
        // Redirect to dashboard
        setTimeout(() => {
          navigate('/dashboard');
        }, 1000);
      }
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.message || 'Invalid username or password. Please try again.' 
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="business-login-page">
      {/* Navigation Bar */}
      <nav className="business-nav">
        <div className="business-nav-container">
          <Link to="/" className="business-nav-logo">
            <div className="business-logo-icon">🏛️</div>
            <span className="business-logo-text">dgprofile.in</span>
          </Link>
          <div className="business-nav-menu">
            <Link to="/" className="business-nav-link">Home</Link>
            <Link to="/register" className="business-nav-link">Register</Link>
            <Link to="/login" className="business-nav-link active">Login</Link>
          </div>
        </div>
      </nav>

      {/* Login Section */}
      <section className="business-login-section">
        <div className="business-login-background">
          <div className="business-login-gradient"></div>
          <div className="business-login-pattern"></div>
        </div>
        <div className="business-login-container">
          <div className="business-login-card">
            <div className="business-login-header">
              <div className="business-login-icon-wrapper">
                <i className="fi fi-rr-lock"></i>
              </div>
              <h1 className="business-login-title">Welcome Back</h1>
              <p className="business-login-subtitle">
                Enter your credentials to access your account
              </p>
            </div>

            {message.text && (
              <div className={`business-alert business-alert-${message.type === 'success' ? 'success' : 'error'}`}>
                <i className={`fi fi-rr-${message.type === 'success' ? 'check-circle' : 'exclamation-circle'}`}></i>
                <span>{message.text}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="business-login-form">
              <div className="business-form-group">
                <label htmlFor="username" className="business-form-label">
                  <i className="fi fi-rr-user"></i>
                  Username
                </label>
                <input
                  type="text"
                  id="username"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  required
                  placeholder="Enter your username"
                  autoComplete="username"
                  className="business-form-input"
                />
              </div>

              <div className="business-form-group">
                <label htmlFor="password" className="business-form-label">
                  <i className="fi fi-rr-lock"></i>
                  Password
                </label>
                <div className="business-password-wrapper">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    placeholder="Enter your password"
                    autoComplete="current-password"
                    className="business-form-input"
                  />
                  <button
                    type="button"
                    className="business-password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label="Toggle password visibility"
                  >
                    <i className={`fi fi-rr-${showPassword ? 'eye-crossed' : 'eye'}`}></i>
                  </button>
                </div>
              </div>

              <div className="business-form-options">
                <label className="business-checkbox-label">
                  <input type="checkbox" className="business-checkbox" />
                  <span>Remember me</span>
                </label>
                <Link to="#" className="business-forgot-link">Forgot password?</Link>
              </div>

              <button 
                type="submit" 
                className="business-login-btn" 
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="business-spinner"></span>
                    <span>Logging in...</span>
                  </>
                ) : (
                  <>
                    <span>Sign In</span>
                    <i className="fi fi-rr-arrow-right"></i>
                  </>
                )}
              </button>
            </form>

            <div className="business-login-divider">
              <span>Or continue with</span>
            </div>

            <div className="business-social-login">
              <button type="button" className="business-social-btn business-social-google">
                <i className="fi fi-brands-google"></i>
                <span>Google</span>
              </button>
              <button type="button" className="business-social-btn business-social-facebook">
                <i className="fi fi-brands-facebook"></i>
                <span>Facebook</span>
              </button>
            </div>

            <div className="business-login-footer">
              <p>
                Don't have an account?{' '}
                <Link to="/register" className="business-register-link">
                  Create an account
                </Link>
              </p>
            </div>
          </div>

          <div className="business-login-visual">
            <div className="business-login-illustration">
              <div className="business-illustration-card">
                <div className="business-illustration-header">
                  <div className="business-illustration-avatar">JD</div>
                  <div>
                    <h3>John Doe</h3>
                    <p>Political Leader</p>
                  </div>
                </div>
                <div className="business-illustration-stats">
                  <div className="business-illustration-stat">
                    <div className="business-stat-icon">👥</div>
                    <div>
                      <div className="business-stat-value">10K+</div>
                      <div className="business-stat-label">Followers</div>
                    </div>
                  </div>
                  <div className="business-illustration-stat">
                    <div className="business-stat-icon">📊</div>
                    <div>
                      <div className="business-stat-value">95%</div>
                      <div className="business-stat-label">Engagement</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="business-login-quote">
              <h3>Join thousands of political leaders</h3>
              <p>Create your professional profile and connect with voters</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Login;
